// Backend API - Cambiar según entorno
const API_BASE_URL = process.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Mock data for when API is unavailable
const MOCK_DATA = {
  health: { status: 'ok', timestamp: new Date().toISOString(), mode: 'mock' },
  profile: (address: string) => ({
    address,
    level: 5,
    xp: 2500,
    achievements: 12,
    lastLogin: new Date().toISOString(),
  }),
  transactions: (address: string) => ({
    transactions: [
      {
        id: '1',
        type: 'swap',
        from: 'ETH',
        to: 'USDC',
        amount: '1.5',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        status: 'completed',
      },
      {
        id: '2',
        type: 'stake',
        token: 'NDH',
        amount: '1000',
        timestamp: new Date(Date.now() - 172800000).toISOString(),
        status: 'completed',
      },
    ],
  }),
  staking: (address: string) => ({
    pools: [
      { id: 'ndh-pool', name: 'NDH Pool', apy: 125, staked: '10000', rewards: '125' },
      { id: 'eth-pool', name: 'ETH Pool', apy: 8.5, staked: '5', rewards: '0.05' },
    ],
  }),
};

// Environment check - MOCK MODE DESHABILITADO PARA PRODUCCION
const USE_MOCK_DATA = process.env.VITE_MOCK_MODE === 'true' || false;

export class APIClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private useMockData: boolean;

  constructor(useMockData: boolean = USE_MOCK_DATA) {
    this.baseURL = API_BASE_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    this.useMockData = useMockData;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    mockFallback?: T
  ): Promise<T> {
    // If mock data is enabled and a fallback is provided, use it directly
    if (this.useMockData && mockFallback !== undefined) {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 300));
      console.log(`🎭 [MOCK MODE] Using mock data for ${endpoint}`);
      return mockFallback;
    }

    const url = `${this.baseURL}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // Only log detailed errors if mock mode is disabled
      if (!this.useMockData) {
        console.error('API Request failed:', {
          url,
          options: config,
          error: error instanceof Error ? error.message : error
        });
      }
      
      // If we have mock fallback, use it silently
      if (mockFallback !== undefined) {
        if (!this.useMockData) {
          console.warn(`⚠️ API request failed, falling back to mock data for ${endpoint}`);
        }
        return mockFallback;
      }
      
      throw error;
    }
  }

  async get<T>(endpoint: string, mockFallback?: T): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, mockFallback);
  }

  async post<T>(endpoint: string, data?: any, mockFallback?: T): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, mockFallback);
  }

  async put<T>(endpoint: string, data?: any, mockFallback?: T): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }, mockFallback);
  }

  async delete<T>(endpoint: string, mockFallback?: T): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, mockFallback);
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string; mode?: string }> {
    return this.get('/health', MOCK_DATA.health);
  }

  // User profile endpoints
  async getProfile(address: string): Promise<any> {
    return this.get(`/profile/${address}`, MOCK_DATA.profile(address));
  }

  async updateProfile(address: string, data: any): Promise<any> {
    return this.post(`/profile/${address}`, data, { ...MOCK_DATA.profile(address), ...data });
  }

  // Transaction endpoints
  async getTransactions(address: string): Promise<any> {
    return this.get(`/transactions/${address}`, MOCK_DATA.transactions(address));
  }

  // Staking endpoints
  async getStakingInfo(address: string): Promise<any> {
    return this.get(`/staking/${address}`, MOCK_DATA.staking(address));
  }

  // Toggle mock mode
  setMockMode(enabled: boolean) {
    this.useMockData = enabled;
  }

  // Check if in mock mode
  isMockMode(): boolean {
    return this.useMockData;
  }
}

export const apiClient = new APIClient();